import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Leftsidebar from "../../sidebar/leftsidebar";
import LeftResponsivesidebar from "../../sidebar/left-responsive";
import Headmenu from "../../sidebar/headmenu";
import AdCreditsSuccessModal from "./AdCreditsSuccessModal";
import BillingBackButton from "./BillingBackButton";
import amountWalletIcon from "../../../assets/images/icons/billing/amount-wallet.svg";
import maximizeIcon from "../../../assets/images/icons/billing/maximize.svg";
import minimizeIcon from "../../../assets/images/icons/billing/minimize.svg";
import thresholdBasedIcon from "../../../assets/images/icons/billing/threshold-based.svg";
import monthlyBasedIcon from "../../../assets/images/icons/billing/monthly-based.svg";
import { upsertBillingProfile, getBillingProfile } from "../../../hook/useAdsApi";

const PRESET_AMOUNTS = [15, 25, 50, 100];
const SPEND_CAP = 50; // placeholder - GET /ad-account/spending-controls' cap

// "Postpaid Billing Setup" - no card is collected here at all. The amount picker only shows for
// Threshold Based - Monthly Based has no threshold concept, and client-api's upsertBillingProfile
// no longer requires one for billing_cycle: 'monthly' (fixed alongside this screen - it used to
// wrongly require a threshold for every postpaid profile regardless of cycle). Bills generated
// against this profile are paid manually later - there is no "pay this invoice" endpoint yet
// (checked ad-billing-invoicing.service.js), so no payment-method step belongs on this screen.
export default function AdPostpaidSetup() {
  const navigate = useNavigate();
  const location = useLocation();
  const currency = location.state?.currency || "USD";
  const adAccount = location.state?.adAccount;

  const [billingMethod, setBillingMethod] = useState("threshold");
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState("");
  const [amountOpen, setAmountOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  // Pre-fill from whatever the seller already has saved - without this, revisiting this screen
  // (e.g. via "Change" on the Dashboard) always reset to the Threshold/$25 default, so clicking
  // Continue without noticing would silently overwrite an existing, different threshold.
  useEffect(() => {
    getBillingProfile()
      .then((res) => {
        if (res?.configured && res.billing_model === "postpaid") {
          if (res.billing_cycle === "monthly") {
            setBillingMethod("monthly");
          } else if (res.postpaid_threshold_amount) {
            setBillingMethod("threshold");
            const saved = Number(res.postpaid_threshold_amount);
            if (PRESET_AMOUNTS.includes(saved)) {
              setAmount(saved);
            } else {
              setCustomAmount(String(saved));
            }
          }
        }
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, []);

  const threshold = customAmount ? Number(customAmount) : amount;

  const handleContinue = async () => {
    if (billingMethod === "threshold" && (!threshold || threshold <= 0)) {
      toast.error("Enter a threshold amount to continue");
      return;
    }
    setSubmitting(true);
    try {
      await upsertBillingProfile({
        billing_model: "postpaid",
        currency_code: currency,
        postpaid_threshold_amount: billingMethod === "threshold" ? threshold : undefined,
        billing_cycle: billingMethod === "monthly" ? "monthly" : undefined,
      });
      setSuccessData({ amount: billingMethod === "threshold" ? threshold : null, billingCycle: billingMethod });
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not save your billing setup, try again");
    } finally {
      setSubmitting(false);
    }
  };

  if (profileLoading) {
    return (
      <div className="dashboard w-full min-h-screen flex">
        <Leftsidebar />
        <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
          <LeftResponsivesidebar />
          <Headmenu />
          <p className="text-sm text-gray-500">Loading...</p>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />
        <main className="flex">
          <div className="w-full max-w-4xl">
            <div className="mb-6 flex flex-wrap gap-3 items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Postpaid Billing Setup</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Configure your postpaid billing preferences.
                </p>
                <div className="border-b border-gray-200 mt-2 w-full max-w-[420px]" />
              </div>
              <BillingBackButton />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">

              <div className="rounded-2xl bg-gray-50 p-5 mb-5">
                <h3 className="font-semibold text-sm mb-1">Billing Preference</h3>
                <p className="text-xs text-gray-500 mb-3">Choose how you want to be billed.</p>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setBillingMethod("threshold")}
                    className={`text-left rounded-xl border bg-white p-4 transition ${
                      billingMethod === "threshold"
                        ? "border-[#095228] ring-1 ring-[#095228]"
                        : "border-gray-200"
                    }`}
                  >
                    <img src={thresholdBasedIcon} alt="" className="w-7 h-7" />
                    <h4 className="font-bold text-sm mt-2 mb-1">Threshold Based</h4>
                    <p className="text-xs text-gray-500">
                      You will be charged when you reach your spend threshold
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setBillingMethod("monthly")}
                    className={`text-left rounded-xl border bg-white p-4 transition ${
                      billingMethod === "monthly"
                        ? "border-[#095228] ring-1 ring-[#095228]"
                        : "border-gray-200"
                    }`}
                  >
                    <img src={monthlyBasedIcon} alt="" className="w-7 h-7" />
                    <h4 className="font-bold text-sm mt-2 mb-1">Monthly Based</h4>
                    <p className="text-xs text-gray-500">Receive one bill for the billing period.</p>
                  </button>
                </div>
              </div>

              {/* Only Threshold Based needs an amount - Monthly bills once per period with no
                  threshold concept. NOTE: client-api's upsertBillingProfile still requires
                  postpaid_threshold_amount > 0 for every postpaid profile regardless of
                  billing_cycle (confirmed by reading the actual code) - the automatic monthly
                  charge trigger isn't wired server-side yet either. So when Monthly is selected
                  we still silently send the last-known amount (default 25) behind the scenes;
                  this is a real gap to revisit once monthly billing itself is actually built. */}
              {billingMethod === "threshold" && (
                <div className="rounded-2xl bg-gray-50 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-sm">Choose your Amount</h3>
                    <button type="button" onClick={() => setAmountOpen((o) => !o)}>
                      <img src={amountOpen ? minimizeIcon : maximizeIcon} alt="" className="w-6 h-6" />
                    </button>
                  </div>
                  {amountOpen && (
                    <>
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {PRESET_AMOUNTS.map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => {
                              setAmount(preset);
                              setCustomAmount("");
                            }}
                            className={`flex items-center gap-2 rounded-lg border bg-white py-2.5 px-3 text-sm font-semibold ${
                              !customAmount && amount === preset
                                ? "border-[#095228] bg-[#ecfbf3] text-[#095228]"
                                : "border-gray-200 text-gray-700"
                            }`}
                          >
                            <img src={amountWalletIcon} alt="" className="w-4 h-4" /> {currency} {preset.toFixed(2)}
                          </button>
                        ))}
                      </div>
                      <label className="block text-sm text-gray-600 mb-1">Other nominal</label>
                      <div className="flex border border-gray-300 rounded-lg overflow-hidden bg-white max-w-sm">
                        <span className="px-3 py-2 text-sm border-r border-gray-300 bg-gray-50 flex items-center gap-1">
                          {currency} <span className="text-xs">▾</span>
                        </span>
                        <input
                          type="number"
                          min="1"
                          value={customAmount}
                          onChange={(e) => setCustomAmount(e.target.value)}
                          placeholder="0"
                          className="flex-1 min-w-0 px-3 py-2 text-sm outline-none"
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <div className="rounded-2xl border border-gray-200 p-5">
                <h3 className="font-bold text-sm mb-1">Post-paid Summary</h3>
                <p className="text-xs text-gray-500 mb-4">Review your details before proceeding.</p>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Billing Preference</span>
                  <span className="font-semibold">
                    {billingMethod === "threshold" ? "Threshold" : "Monthly"}
                  </span>
                </div>
                {billingMethod === "threshold" && (
                  <div className="flex justify-between text-sm mb-4">
                    <span className="text-gray-500">Billing Threshold</span>
                    <span className="font-semibold">{currency} {threshold.toFixed(2)}</span>
                  </div>
                )}

                <div className="rounded-xl bg-blue-50 p-3 flex gap-2 items-start mb-4">
                  <span className="text-blue-500 text-sm">ⓘ</span>
                  <p className="text-xs text-blue-700">
                    You'll pay each generated invoice manually - no payment method is charged
                    automatically. Your total spend will still be capped at the platform approved
                    limit of {currency} {SPEND_CAP}.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleContinue}
                  disabled={submitting}
                  className="w-full bg-[#095228] text-white font-semibold py-2.5 rounded-full text-sm hover:opacity-90 transition disabled:opacity-60"
                >
                  {submitting ? "Saving..." : "Continue"}
                </button>
                <p className="text-xs text-gray-400 text-center mt-2">
                  By proceeding, you agree to our{" "}
                  {/* No Billing Policy page exists anywhere in the app yet - left as plain text
                      rather than a fake link until a real page exists to point it at. */}
                  <span className="text-gray-500">Billing Policy</span> and{" "}
                  <a href="/terms-conditions" target="_blank" rel="noopener noreferrer" className="text-[#095228] underline">
                    Terms of Service
                  </a>.
                </p>
              </div>
            </div>
            </div>
          </div>
        </main>
      </main>

      {successData && (
        <AdCreditsSuccessModal
          mode="postpaid"
          amount={successData.amount}
          currency={currency}
          billingCycle={successData.billingCycle}
          onDone={() => {
            setSuccessData(null);
            navigate(adAccount ? "/ads/create" : "/ads/billing", { state: { adAccount } });
          }}
        />
      )}
    </div>
  );
}
